import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader, PageLoader } from '@/components/common/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { fmtCoord } from '@/lib/format';
import { CategoryBadge } from '@/components/common/CategoryBadge';
import { RiskBadge } from '@/components/common/RiskBadge';
import { Camera, RefreshCcw, MapPin, ClipboardCheck } from 'lucide-react';

export default function FieldVerifyPage() {
  const { incidentId } = useParams();
  const nav = useNavigate();
  const [inc, setInc] = useState(null);
  const [verified, setVerified] = useState(true);
  const [action, setAction] = useState('');
  const [notes, setNotes] = useState('');
  const [gps, setGps] = useState({ lat: null, lng: null });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/incidents/${incidentId}`).then(r => setInc(r.data));
    fetchGps();
  }, [incidentId]);

  const fetchGps = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {}
    );
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6);
    const b64s = await Promise.all(files.map(f => new Promise((res) => {
      const rd = new FileReader();
      rd.onload = () => res(String(rd.result).split(',')[1] || '');
      rd.readAsDataURL(f);
    })));
    setPhotos([...photos, ...b64s]);
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/incidents/${incidentId}/verify`, {
        incident_id: incidentId,
        verified,
        action_taken: action,
        notes,
        gps_lat: gps.lat,
        gps_lng: gps.lng,
        photos_b64: photos,
      });
      toast.success('Verification submitted');
      nav(`/app/incidents/${incidentId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submission failed');
    } finally { setBusy(false); }
  };

  if (!inc) return <div className="p-6"><PageLoader /></div>;

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto pb-24" data-testid="field-verification-form">
      <PageHeader
        title="Field verification"
        subtitle={<span className="font-mono">{inc.number}</span>}
        actions={<><CategoryBadge category={inc.category} /><RiskBadge severity={inc.severity} /></>}
      />

      <Card className="p-4">
        <div className="text-sm font-semibold">{inc.title}</div>
        <div className="mt-1 text-xs text-muted-foreground">{inc.description}</div>
      </Card>

      <Card className="p-4 mt-4">
        <div className="text-sm font-semibold mb-3">Location</div>
        <div className="flex items-center gap-2">
          <MapPin size={14} className="text-primary" />
          <div className="font-mono text-sm" data-testid="field-verification-gps-readout">{gps.lat ? `${fmtCoord(gps.lat)}, ${fmtCoord(gps.lng)}` : 'Not captured'}</div>
          <Button size="sm" variant="outline" onClick={fetchGps} className="ml-auto"><RefreshCcw size={14} className="mr-1" /> Refresh</Button>
        </div>
      </Card>

      <Card className="p-4 mt-4">
        <div className="text-sm font-semibold mb-3">Photos</div>
        <label htmlFor="photo-in" className="block">
          <div className="cursor-pointer border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary transition-colors">
            <Camera size={22} className="mx-auto text-primary" />
            <div className="mt-2 text-sm">Tap to add photos</div>
            <div className="text-xs text-muted-foreground">JPEG/PNG · up to 6</div>
          </div>
          <input id="photo-in" type="file" accept="image/*" multiple className="hidden" onChange={onFiles} data-testid="field-verification-photo-input" />
        </label>
        {photos.length > 0 && (
          <div className="mt-3 grid grid-cols-3 gap-2">
            {photos.map((b, i) => (
              <div key={i} className="relative rounded-lg overflow-hidden border border-border">
                <img src={`data:image/jpeg;base64,${b}`} alt="upload" className="w-full h-24 object-cover" />
                <button onClick={() => setPhotos(photos.filter((_, ix) => ix !== i))}
                        className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/70 text-white text-xs">×</button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-4 mt-4 space-y-3">
        <div className="text-sm font-semibold">Decision</div>
        <div className="flex items-center gap-3">
          <Switch checked={verified} onCheckedChange={setVerified} data-testid="field-verification-verify-toggle" />
          <Label>{verified ? 'Verified — change confirmed' : 'Not verified — no change / false positive'}</Label>
        </div>
        <div>
          <Label>Action taken</Label>
          <Input value={action} onChange={e => setAction(e.target.value)} placeholder="e.g., area cordoned, informed field officer" />
        </div>
        <div>
          <Label>Notes</Label>
          <Textarea rows={4} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Detailed observations…" data-testid="field-verification-notes-input" />
        </div>
      </Card>

      <div className="fixed bottom-0 inset-x-0 border-t border-border bg-background/95 backdrop-blur p-3 flex justify-end gap-2">
        <Button variant="outline" onClick={() => nav(-1)}>Cancel</Button>
        <Button onClick={submit} disabled={busy} data-testid="field-verification-submit-button"><ClipboardCheck size={14} className="mr-1" />{busy ? 'Submitting…' : 'Submit verification'}</Button>
      </div>
    </div>
  );
}
